// Central config. Everything tunable lives here or in .env (VITE_* vars are
// exposed to the client by Vite). Secrets like the OpenClaw token come in via
// .env and are NEVER hardcoded — Phase 1 reads them from here.

const env = import.meta.env || {} // {} outside Vite (e.g. node --test) so imports don't crash

// Ready Player Me avatar. Paste YOUR .glb URL into .env as VITE_RPM_AVATAR_URL.
// It must include the morph targets the later phases need:
//   ?morphTargets=ARKit,Oculus%20Visemes
//
// The fallback is a LOCAL fullbody VRM (public/avatar.vrm) so the app renders a
// real character out of the box with no CDN dependency — it has visemes,
// emotions and blink, so lip-sync + emotion + idle all work. Drop in your own
// VRM, or a fullbody RPM .glb URL via .env / ?avatar=, for a different avatar.
const FALLBACK_AVATAR = '/avatar.vrm'

// Ready Player Me .glb URLs need these morph targets for lip-sync + emotion.
// If you paste a bare RPM URL we append them automatically.
const REQUIRED_MORPHS = 'morphTargets=ARKit,Oculus%20Visemes'
function ensureMorphTargets(url) {
  if (!url || !/readyplayer\.me|\.glb/i.test(url)) return url
  if (/morphTargets=/i.test(url)) return url
  return url + (url.includes('?') ? '&' : '?') + REQUIRED_MORPHS
}

// Quick override: open http://localhost:5174/?avatar=<rpm-url> to try an avatar
// instantly — no .env edit, no restart. Falls back to .env, then the local head.
const urlOverride =
  typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('avatar')
    : null

const chosenAvatar = urlOverride || env.VITE_RPM_AVATAR_URL || FALLBACK_AVATAR

export const config = {
  // --- Avatar ---
  avatarUrl: ensureMorphTargets(chosenAvatar),

  // --- OpenClaw (Phase 1, placeholders for now) ---
  openClawBaseUrl: env.VITE_OPENCLAW_BASE_URL || '',
  openClawToken: env.VITE_OPENCLAW_TOKEN || '',
  openClawModel: env.VITE_OPENCLAW_MODEL || 'default',

  // --- TTS (Phase 1) — default is the free browser engine ---
  ttsProvider: env.VITE_TTS_PROVIDER || 'speechSynthesis',

  // --- Story Mode (EPIC-15) ---
  storyMode: env.VITE_STORY_MODE !== 'false',
  storiesCatalogUrl: env.VITE_STORIES_URL || '/api/v1/stories.json',

  // --- Companion Mode (EPIC-16) — proactive, persistent free-roam ---
  // Off → free roam is byte-identical to today. On → a Companion Director
  // notices you, remembers across sessions, and reaches out (warmly, not naggy).
  companionMode: env.VITE_COMPANION_MODE === 'true',

  // --- Sessions (EPIC-18) — save / resume / share a playthrough ---
  // Off → today's behaviour (Story Mode ephemeral, Companion Mode one singleton
  // blob per world). On → playthroughs auto-save to a local SessionStore, one
  // story spawns many resumable saves, and sessions export/import as files.
  sessions: env.VITE_SESSIONS === 'true' && !(typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('studioPreview') === '1'),

  // --- Scene ---
  // Comfortable standing distance/scale for the avatar in VR (meters).
  avatarPosition: { x: -2.2, y: 0, z: -1.6 }, // stage center; left of the loft bar (x≈0) on open floor
  cameraStart: { x: -1.75, y: 1.6, z: 0.9 }, // ~eye height, in front of + centered on the 2-character arc

  // --- Environment (static world the characters live in) ---
  // A glTF backdrop loaded into the scene; replaces the procedural floor/grid.
  // It's recentered to the origin and grounded at y=0; tweak the knobs to taste.
  // Set `url` to '' (or environment: null) to fall back to the placeholder room.
  environment: {
    url: '/environments/low_poly_house_interior.glb',
    scale: 1, // model is already in meters
    rotationY: 0, // radians, spin to orient the interior toward the user
    position: { x: 0, y: 0, z: 0 }, // extra offset after recenter+ground
    recenter: true,
  },
}

export const usingFallbackAvatar = chosenAvatar === FALLBACK_AVATAR
